import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProduct, getAddons, getBookingSlots, getReservedDatesForProduct, getBlockedDatesForProduct } from '../services/api';
import { Product, Addon, BookingSlot } from '../types';
import { ArrowLeftIcon, CalendarIcon, DollarSignIcon, CheckIcon, XIcon, CircleIcon, PlusIcon } from 'lucide-react';
import { format, addDays } from 'date-fns';
import ImageCarousel from '../components/ImageCarousel';
import DateRangePicker from '../components/DateRangePicker';
import { saveDatesToStorage, loadDatesFromStorage, clearDatesFromStorage } from '../utils/dateStorage';
import { getContent } from '../config/content';
import { theme } from '../config/theme';

const ProductDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const productId = location.state?.productId;
  
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStartDate, setSelectedStartDate] = useState('');
  const [selectedEndDate, setSelectedEndDate] = useState('');
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [dateAvailability, setDateAvailability] = useState<{ [date: string]: boolean }>({});
  const [addons, setAddons] = useState<Addon[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<{addonId: string, selectedOption?: {label: string, priceModifier?: number, image?: string}}[]>([]);

  useEffect(() => {
    if (productId) {
      fetchProduct();
      // Load saved dates on component mount
      const savedDates = loadDatesFromStorage();
      const today = format(new Date(), 'yyyy-MM-dd');
      if (savedDates.startDate && savedDates.startDate < today) {
        // Saved date is in the past — drop it so it doesn't pre-fill the picker
        clearDatesFromStorage();
      } else if (savedDates.startDate && savedDates.endDate) {
        setSelectedStartDate(savedDates.startDate);
        setSelectedEndDate(savedDates.endDate);
      }
    } else {
      // No productId in state, redirect to home
      navigate('/');
    }
  }, [productId]);

  useEffect(() => {
    if (selectedStartDate && selectedEndDate && product) {
      checkAvailability();
      checkAvailabilityForCalendar(); // Also populate full year datepicker colors
      // Save dates to localStorage whenever they change
      saveDatesToStorage(selectedStartDate, selectedEndDate);
    } else if (selectedStartDate && product) {
      // Only start date selected - update calendar availability
      checkAvailabilityForCalendar();
      setIsAvailable(null);
    } else if (product) {
      // Load initial availability when product loads (no dates selected yet)
      loadInitialAvailability();
    }
  }, [selectedStartDate, selectedEndDate, product]);

  // Re-check availability when addons change (for next day pickup logic)
  useEffect(() => {
    if (selectedStartDate && selectedEndDate && product) {
      checkAvailability();
    }
  }, [selectedAddons]);

  // Additional useEffect to handle case where product loads after dates are already selected
  useEffect(() => {
    if (product && selectedStartDate && !dateAvailability[selectedStartDate]) {
      // Product is loaded and dates are selected but availability isn't populated yet
      checkAvailabilityForCalendar();
    }
  }, [product, selectedStartDate, selectedEndDate]);

  
  const fetchProduct = async () => {
    if (!productId) return;
    try {
      const data = await getProduct(productId);
      setProduct(data);
      fetchAddons(data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddons = async (productData: Product) => {
    if (!productData) return;
    
    try {
      const data = await getAddons();
      // Filter addons to show those linked to this product OR the next day pickup addon
      const filteredAddons = data.filter((addon: Addon) => 
        (productData.linked_addons && productData.linked_addons.includes(addon.addon_id)) ||
        addon.name.toLowerCase().trim() === 'next day pickup'
      );
      setAddons(filteredAddons);
    } catch (error) {
      console.error('Error fetching addons:', error);
    }
  };

  const loadInitialAvailability = async () => {
    if (!product) return;
    
    try {
      // Get reserved dates for the full year using the fast reserved dates API
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + 365); // 1 year ahead
      
      const startDateStr = format(today, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      
      // Fetch reserved dates (much faster than checking availability)
      const reservedDates = await getReservedDatesForProduct(product.product_id, startDateStr, endDateStr);
      
      // Initialize all dates as available, then mark reserved dates as unavailable
      const dateAvailMap: { [date: string]: boolean } = {};
      const currentDate = new Date(today);
      
      while (currentDate <= endDate) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        dateAvailMap[dateStr] = true; // Assume available initially
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Mark reserved dates as unavailable
      reservedDates.forEach((reservedDate: string) => {
        dateAvailMap[reservedDate] = false; // Mark as unavailable
      });
      
      setDateAvailability(dateAvailMap);
    } catch (error) {
      console.error('Error loading initial availability:', error);
    }
  };

  const checkAvailabilityForCalendar = async () => {
    if (!product || !selectedStartDate) return;
    
    try {
      // Get reserved dates for full year starting from TODAY (not selected start date)
      const startDateObj = new Date(); // Start from today
      const endDateObj = new Date(startDateObj);
      endDateObj.setDate(startDateObj.getDate() + 365); // 1 year ahead
      
      // Use UTC date formatting to prevent timezone shifting
      const startDateStr = startDateObj.toISOString().split('T')[0];
      const endDateStr = endDateObj.toISOString().split('T')[0];
      
      // Fetch reserved dates and blocked dates (much faster than checking availability)
      const [reservedDates, blockedDates] = await Promise.all([
        getReservedDatesForProduct(product.product_id, startDateStr, endDateStr),
        getBlockedDatesForProduct(product.product_id, startDateStr, endDateStr)
      ]);
      
      // Initialize all dates as available, then mark reserved/blocked dates as unavailable
      const dateAvailMap: { [date: string]: boolean } = {};
      const currentDate = new Date(startDateStr + 'T00:00:00Z');
      
      while (currentDate <= new Date(endDateStr + 'T00:00:00Z')) {
        const dateStr = currentDate.toISOString().split('T')[0];
        dateAvailMap[dateStr] = true; // Assume available initially
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Mark reserved dates as unavailable
      reservedDates.forEach((reservedDate: string) => {
        dateAvailMap[reservedDate] = false; // Mark as unavailable
      });
      
      // Mark blocked dates as unavailable
      blockedDates.forEach((blockedDate: string) => {
        dateAvailMap[blockedDate] = false; // Mark as unavailable
      });
      
      setDateAvailability(dateAvailMap);
    } catch (error) {
      console.error('Error checking availability for calendar:', error);
    }
  };

  // Check if next day is available for next day pickup addon (using existing dateAvailability data)
  const isNextDayAvailable = () => {
    if (!selectedEndDate) return true;
    
    // If dateAvailability data hasn't loaded yet, assume available
    if (Object.keys(dateAvailability).length === 0) return true;
    
    try {
      // Calculate next day
      const endDate = new Date(selectedEndDate + 'T00:00:00Z');
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextDayStr = nextDay.toISOString().split('T')[0];
      
      // Check if next day is available using existing dateAvailability data
      return dateAvailability[nextDayStr] !== false; // false means unavailable
    } catch (error) {
      console.error('Error checking next day availability:', error);
      return true; // Default to available if check fails
    }
  };

  const [nextDayAvailability, setNextDayAvailability] = useState<boolean | null>(null);

  // Check next day availability when dates change
  useEffect(() => {
    if (selectedEndDate) {
      const available = isNextDayAvailable();
      setNextDayAvailability(available);
      
      // Auto-deselect next day pickup addon if next day becomes unavailable
      if (!available) {
        const nextDayPickupAddon = selectedAddons.find(sa => {
          const addon = addons.find(a => a.addon_id === sa.addonId);
          return addon && addon.name.toLowerCase().trim() === 'next day pickup';
        });
        
        if (nextDayPickupAddon) {
          setSelectedAddons(selectedAddons.filter(sa => sa.addonId !== nextDayPickupAddon.addonId));
        }
      }
    } else {
      setNextDayAvailability(null);
    }
  }, [selectedEndDate, dateAvailability, selectedAddons, addons]);

  const checkAvailability = async () => {
    if (!product || !selectedStartDate || !selectedEndDate) return;
    
    setCheckingAvailability(true);
    try {
      // Use UTC date formatting to prevent timezone shifting
      const startDateStr = new Date(selectedStartDate + 'T00:00:00Z').toISOString().split('T')[0];
      let endDateStr = new Date(selectedEndDate + 'T00:00:00Z').toISOString().split('T')[0];
      
      // Check if next day pickup addon is selected and extend the end date
      const hasNextDayPickup = selectedAddons.some(selectedAddon => {
        const addon = addons.find(a => a.addon_id === selectedAddon.addonId);
        return addon && addon.name.toLowerCase().trim() === 'next day pickup';
      });
      
      if (hasNextDayPickup) {
        // Extend end date by one day for availability checking
        const endDate = new Date(endDateStr + 'T00:00:00Z');
        endDate.setDate(endDate.getDate() + 1);
        endDateStr = endDate.toISOString().split('T')[0];
      }
      
      // Fetch reserved dates for the selected date range (extended if next day pickup)
      const reservedDates = await getReservedDatesForProduct(product.product_id, startDateStr, endDateStr);
      
      // Product is available if no dates in the range are reserved
      const hasReservedDates = reservedDates.length > 0;
      setIsAvailable(!hasReservedDates);

      // Update date availability map based on reserved dates
      const dateAvailMap: { [date: string]: boolean } = {};
      const currentDate = new Date(startDateStr + 'T00:00:00Z');
      
      while (currentDate <= new Date(endDateStr + 'T00:00:00Z')) {
        const dateStr = currentDate.toISOString().split('T')[0];
        dateAvailMap[dateStr] = !reservedDates.includes(dateStr);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      setDateAvailability(dateAvailMap);
    } catch (error) {
      console.error('Error checking availability:', error);
      setIsAvailable(true); // Default to available if check fails
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Check if any selected addons have options but no option selected
  const hasInvalidAddonSelection = () => {
    return selectedAddons.some(selectedAddon => {
      const addon = addons.find(a => a.addon_id === selectedAddon.addonId);
      return addon && addon.options && addon.options.length > 0 && !selectedAddon.selectedOption;
    });
  };

  const handleBookNow = () => {
    if (!product || !selectedStartDate || !selectedEndDate) return;
    
    // Validate that all selected addons with options have a selected option
    const invalidAddons = selectedAddons.filter(selectedAddon => {
      const addon = addons.find(a => a.addon_id === selectedAddon.addonId);
      return addon && addon.options && addon.options.length > 0 && !selectedAddon.selectedOption;
    });
    
    if (invalidAddons.length > 0) {
      // Find the first invalid addon name for the error message
      const invalidAddonName = addons.find(a => a.addon_id === invalidAddons[0].addonId)?.name || 'Addon';
      alert(`Please select an option for ${invalidAddonName} before proceeding.`);
      return;
    }
    
    // Navigate to booking page with pre-filled data
    navigate('/booking', {
      state: {
        productId: product.product_id,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        product: product,
        selectedAddons: selectedAddons
      }
    });
  };

  const getToday = () => format(new Date(), 'yyyy-MM-dd');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <p className="text-gray-600 mb-6">The product you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/rentals')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            {getContent('productDetails.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/rentals')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Rentals
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Product Images */}
          <div className="relative sm:mb-0" style={{ marginBottom: '3.2rem' }}>
            <div className="w-full h-[32rem] sm:h-[40rem] lg:h-[48rem]">
              <ImageCarousel
                images={product.images}
                alt={product.name}
                className="w-full h-full"
              />
            </div>
          </div>

          {/* Product Details */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
              <div className="flex items-center" style={{ color: theme.colors.primary[700] }}>
                <span className="text-2xl font-semibold whitespace-nowrap">
                  ${product.price ? (product.price.amount_cents / 100) : 0}/day
                </span>
              </div>
            </div>

            <div className="prose prose-gray mb-8">
              <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{product.description}</p>
            </div>

            {/* Date Selection */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{getContent('productDetails.selectDates')}</h3>
              
              <DateRangePicker
                startDate={selectedStartDate}
                endDate={selectedEndDate}
                onStartDateChange={setSelectedStartDate}
                onEndDateChange={setSelectedEndDate}
                onClearDates={() => {
                  // Clear dates and reset availability status
                  setSelectedStartDate('');
                  setSelectedEndDate('');
                  setIsAvailable(null);
                  // Clear dates from localStorage
                  clearDatesFromStorage();
                }}
                minDate={getToday()}
                maxDays={7}
                availabilityMap={dateAvailability}
                productId={product?.product_id}
                preserveOpenState={true}
                disableToday={true}
              />

              {selectedStartDate && selectedEndDate && (
                <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#dbeafe' }}>
                  <p className="text-sm" style={{ color: '#1e40af' }}>
                    <CalendarIcon className="inline h-4 w-4 mr-1" />
                    Rental period: {format(new Date(selectedStartDate + 'T00:00:00'), 'MMM d, yyyy')} - {format(new Date(selectedEndDate + 'T00:00:00'), 'MMM d, yyyy')}
                  </p>
                </div>
              )}
            </div>

            {/* Availability Status */}
            {isAvailable !== null && (
              <div className={`rounded-lg p-4 mb-6 ${
                isAvailable
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center">
                  {isAvailable ? (
                    <>
                      <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                      <span className="text-green-800 font-medium">{getContent('productDetails.available')}</span>
                    </>
                  ) : (
                    <>
                      <XIcon className="h-5 w-5 text-red-600 mr-2" />
                      <span className="text-red-800 font-medium">{getContent('productDetails.notAvailable')}</span>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Date Selection Prompt */}
            {isAvailable === null && (
              <div className="rounded-lg p-4 mb-6 bg-blue-50 border border-blue-200">
                <div className="flex items-center">
                  <CalendarIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-blue-800 font-medium">Select dates to see availability</span>
                </div>
              </div>
            )}

            {/* Addon Selection */}
            {(() => {
              const filteredAddons = addons.filter(addon => 
                product && (
                  (product.linked_addons && product.linked_addons.includes(addon.addon_id)) ||
                  addon.name.toLowerCase().trim() === 'next day pickup'
                )
              );
              return filteredAddons.length > 0;
            })() && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add-ons</h3>
                <div className="space-y-3">
                  {addons
                    .filter(addon => 
                      product && (
                        (product.linked_addons && product.linked_addons.includes(addon.addon_id)) ||
                        addon.name.toLowerCase().trim() === 'next day pickup'
                      )
                    )
                    .map((addon) => {
                      const selectedAddon = selectedAddons.find(sa => sa.addonId === addon.addon_id);
                      const isSelected = !!selectedAddon;
                      
                      // Determine which image to show - option image if selected, otherwise default addon image
                      const displayImage = selectedAddon?.selectedOption?.image || 
                                          (addon.images && addon.images.length > 0 ? addon.images[0] : null);
                      
                      return (
                        <div key={addon.addon_id} className="border border-gray-200 rounded-lg p-4">
                          {/* Mobile Layout: Stack vertically */}
                          <div className="block sm:hidden">
                            <div className="flex items-center space-x-3 mb-3">
                              {displayImage ? (
                                <img
                                  src={displayImage}
                                  alt={addon.name}
                                  className="h-20 w-20 rounded-lg object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className="h-20 w-20 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                                  <span className="text-gray-400 text-xs">No image</span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900">{addon.name}</h4>
                                <p className="text-sm text-gray-500 mt-1">{addon.description}</p>
                                {addon.name.toLowerCase().trim() === 'next day pickup' && nextDayAvailability === false && (
                                  <p className="text-xs text-red-600 mt-1">
                                    ⚠️ Next day is already booked
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                              <span className="text-base font-semibold text-gray-900">
                                ${(parseFloat(String(addon.price)) / 100).toFixed(2)}
                              </span>
                              {addon.name.toLowerCase().trim() === 'next day pickup' && nextDayAvailability === false ? (
                                <button
                                  type="button"
                                  disabled
                                  className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
                                >
                                  <XIcon className="h-4 w-4 inline mr-1" />
                                  Not Available
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedAddons(selectedAddons.filter(sa => sa.addonId !== addon.addon_id));
                                    } else {
                                      setSelectedAddons([...selectedAddons, { addonId: addon.addon_id }]);
                                    }
                                  }}
                                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                                    isSelected
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  {isSelected ? (
                                    <>
                                      <CheckIcon className="h-4 w-4 inline mr-1" />
                                      Added
                                    </>
                                  ) : (
                                    <>
                                      <PlusIcon className="h-4 w-4 inline mr-1" />
                                      Add
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* Desktop Layout: Original horizontal layout */}
                          <div className="hidden sm:flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              {displayImage ? (
                                <img
                                  src={displayImage}
                                  alt={addon.name}
                                  className="h-20 w-20 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="h-20 w-20 rounded-lg bg-gray-200 flex items-center justify-center">
                                  <span className="text-gray-400 text-xs">No image</span>
                                </div>
                              )}
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{addon.name}</h4>
                                <p className="text-sm text-gray-500">{addon.description}</p>
                                {addon.name.toLowerCase().trim() === 'next day pickup' && nextDayAvailability === false && (
                                  <p className="text-xs text-red-600 mt-1">
                                    ⚠️ Next day is already booked
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-900">
                                ${(parseFloat(String(addon.price)) / 100).toFixed(2)}
                              </span>
                              {addon.name.toLowerCase().trim() === 'next day pickup' && nextDayAvailability === false ? (
                                <div className="text-right">
                                  <button
                                    type="button"
                                    disabled
                                    className="px-3 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
                                  >
                                    <XIcon className="h-3 w-3 mr-1" />
                                    Not Available
                                  </button>
                                  <p className="text-xs text-red-600 mt-1">
                                    Not available for selected date(s)
                                  </p>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedAddons(selectedAddons.filter(sa => sa.addonId !== addon.addon_id));
                                    } else {
                                      setSelectedAddons([...selectedAddons, { addonId: addon.addon_id }]);
                                    }
                                  }}
                                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                                    isSelected
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  {isSelected ? (
                                    <>
                                      <CheckIcon className="h-3 w-3 mr-1" />
                                      Added
                                    </>
                                  ) : (
                                    <>
                                      <PlusIcon className="h-3 w-3 mr-1" />
                                      Add
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* Options Section */}
                          {isSelected && addon.options && addon.options.length > 0 && (
                            <div className="border-t pt-3 mt-3">
                              <h5 className="text-sm font-medium text-gray-700 mb-2">
                                {addon.option_label || 'Select Option:'}
                              </h5>
                              <select
                                value={selectedAddon.selectedOption?.label || ''}
                                onChange={(e) => {
                                  const selectedOption = addon.options.find(opt => opt.label === e.target.value);
                                  const updatedAddons = selectedAddons.map(sa =>
                                    sa.addonId === addon.addon_id
                                      ? { ...sa, selectedOption: selectedOption || undefined }
                                      : sa
                                  );
                                  setSelectedAddons(updatedAddons);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">No option selected</option>
                                {addon.options.map((option, index) => (
                                  <option key={index} value={option.label}>
                                    {option.label}
                                    {(option.priceModifier || option.price_modifier) && ` (+$${option.priceModifier || option.price_modifier})`}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Book Now Button */}
            <button
              onClick={handleBookNow}
              disabled={!selectedStartDate || !selectedEndDate || isAvailable === false || checkingAvailability || hasInvalidAddonSelection()}
              className={`w-full py-3 px-6 rounded-md font-semibold transition-colors ${
                !selectedStartDate || !selectedEndDate
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : isAvailable === false
                  ? 'bg-red-300 text-red-700 cursor-not-allowed'
                  : checkingAvailability
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : hasInvalidAddonSelection()
                  ? 'bg-orange-300 text-orange-700 cursor-not-allowed'
                  : 'text-white'
              } ${!isAvailable || checkingAvailability || hasInvalidAddonSelection() ? '' : 'hover:opacity-90'}`}
              style={!isAvailable && !checkingAvailability && !hasInvalidAddonSelection() ? {} : checkingAvailability || hasInvalidAddonSelection() ? {} : { backgroundColor: theme.colors.primary[800] }}
            >
              {!selectedStartDate || !selectedEndDate
                ? getContent('productDetails.selectDatesFirst')
                : checkingAvailability
                ? getContent('productDetails.checkingAvailability')
                : isAvailable === false
                ? getContent('productDetails.notAvailable')
                : hasInvalidAddonSelection()
                ? 'Select addon options to continue'
                : getContent('productDetails.bookNow')}
            </button>

            {/* Additional Information */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{getContent('productDetails.rentalInfo.title')}</h3>
              <div className="space-y-4 text-sm text-gray-600">
                {Object.keys(getContent('productDetails.rentalInfo')).filter(key => key.startsWith('option')).map((optionKey, index) => {
                  const option = getContent(`productDetails.rentalInfo.${optionKey}`);
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-start">
                        <CircleIcon className="h-4 w-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="font-medium">{option.main}</span>
                      </div>
                      {option.subBullet1 && (
                        <div className="ml-6 flex items-start">
                          <div className="h-1.5 w-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                          <span>{option.subBullet1}</span>
                        </div>
                      )}
                      {option.subBullet2 && (
                        <div className="ml-6 flex items-start">
                          <div className="h-1.5 w-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                          <span>{option.subBullet2}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
